import { useMemo } from 'react';

import type { PetitionUpdatesList } from '#src/app/pages/petitionDetails/shared/types';
import type { State } from '#src/app/shared/utils/async';

import { useCreationUpdate } from '../useCreationUpdate';
import { useDisplayedUpdates } from '../useDisplayedUpdates';
import { useFetchMore } from '../useFetchMore';
import { usePetitionUpdatesQuery } from '../usePetitionUpdatesQuery';
import { useQueryState } from '../useQueryState';
import { useVictoryUpdate } from '../useVictoryUpdate';

const LOADING_STATE = { status: 'loading' } as const;
const ERROR_STATE = { status: 'error' } as const;

type PetitionUpdatesState = State<{ updates: PetitionUpdatesList; loadingMore: boolean; fetchMore?: () => void }>;

function isLoadingMore(queryStateCursor: string | null | undefined, updatesStateEndCursor: string | null) {
	return queryStateCursor !== null && queryStateCursor === updatesStateEndCursor;
}

export function usePetitionUpdates(): PetitionUpdatesState {
	const creationUpdate = useCreationUpdate();
	const victoryUpdate = useVictoryUpdate();

	// query state is updated by fetchMore to trigger a new query
	const [queryState, setQueryState] = useQueryState();

	// this will query the API based on the query state
	const updatesState = usePetitionUpdatesQuery(queryState);

	// this "hides" updates (milestones) based on business logic
	const filteredUpdates = useDisplayedUpdates(updatesState.data);

	const fetchMore = useFetchMore(updatesState.data, filteredUpdates, setQueryState);

	const { data: updatesData, isError } = updatesState;

	return useMemo(() => {
		if (!updatesData) {
			return isError ? ERROR_STATE : LOADING_STATE;
		}
		const { displayedCount } = updatesData;
		const truncatedUpdates = filteredUpdates.slice(0, displayedCount);
		const truncatedUpdatesWithVictoryUpdate = [...(victoryUpdate ? [victoryUpdate] : []), ...truncatedUpdates];
		const updates = fetchMore
			? truncatedUpdatesWithVictoryUpdate
			: [
					...truncatedUpdatesWithVictoryUpdate,
					// last page, let's add the creation update
					creationUpdate,
				];
		return {
			status: 'loaded',
			updates,
			loadingMore: isLoadingMore(queryState.cursor, updatesData.endCursor),
			fetchMore,
		};
	}, [updatesData, isError, queryState, filteredUpdates, fetchMore, creationUpdate, victoryUpdate]);
}
